import React, { useEffect, useState } from 'react';
import { Alert, Button, Drawer, Space, Tabs, Tag, Typography, theme } from 'antd';
import {
  ApiOutlined,
  BellOutlined,
  CalendarOutlined,
  PartitionOutlined,
  ProfileOutlined,
  QrcodeOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import QrEquipoModal from './QrEquipoModal';
import AlertasEquipo from './AlertasEquipo';
import CertificacionesList from './CertificacionesList';
import ComponentesEquipo from './ComponentesEquipo';
import EquipoDatosForm from './EquipoDatosForm';
import useTenantFlags from 'hooks/useTenantFlags';
import { ProLockOverlay } from 'components/global/ProLock';
import { TIPO_EQUIPO_COLORS, TIPO_EQUIPO_LABELS } from './tipoEquipoCategoria';

const { Title, Text } = Typography;
const { useToken } = theme;

// Drawer de detalle de un equipo con 4 tabs. En Fase 2 los tabs "Partes
// mensuales" y "Alertas" son placeholders que apuntan a Fase 3/4; el flujo
// queda cableado para poblarse sin cambiar layout.
function EquipoDetailDrawer({ open, equipo, idCliente, defaultTab, onClose, onSaved }) {
  const { token } = useToken();
  const { isPro } = useTenantFlags();
  const [activeTab, setActiveTab] = useState(defaultTab ?? 'datos');
  const [qrOpen, setQrOpen] = useState(false);

  // Cada vez que se abre el drawer o cambia el tab solicitado por el caller,
  // resetear el activeTab. Usado por la columna "Alertas" de AscensoresPage
  // para abrir directo en el tab 'alertas'.
  useEffect(() => {
    if (open) setActiveTab(defaultTab ?? 'datos');
  }, [open, defaultTab, equipo?.id]);

  if (!equipo) return null;

  const tabItems = [
    {
      key: 'datos',
      label: (
        <span>
          <ProfileOutlined /> Datos
        </span>
      ),
      children: (
        <EquipoDatosForm
          equipo={equipo}
          idCliente={idCliente}
          onSaved={(updated) => onSaved?.(updated)}
        />
      ),
    },
    {
      key: 'certificaciones',
      label: (
        <span>
          <SafetyCertificateOutlined /> Certificaciones
        </span>
      ),
      children: <CertificacionesList idEquipo={equipo.id} />,
    },
    {
      key: 'partes',
      label: (
        <span>
          <CalendarOutlined /> Partes mensuales
        </span>
      ),
      children: (
        <Alert
          type="info"
          showIcon
          message="Próximamente"
          description="La lista de partes mensuales por equipo se activa al cablear id_equipo a las mantenciones (Fase 3 del plan regulatorio). Mientras tanto, los partes viven asociados al cliente y se ven en el tab 'Mantenciones'."
        />
      ),
    },
    {
      key: 'alertas',
      label: (
        <span>
          <BellOutlined /> Alertas
        </span>
      ),
      children: <AlertasEquipo idEquipo={equipo.id} />,
    },
    // El árbol de componentes es feature pro (backend la gatea con
    // requirePlan('pro')). En lite el tab queda visible con candado para
    // comunicar el valor, sin montar ComponentesEquipo ni disparar sus fetches.
    {
      key: 'componentes',
      label: (
        <span>
          <PartitionOutlined /> Componentes
        </span>
      ),
      children: (
        <ProLockOverlay
          locked={!isPro}
          title="Árbol de componentes"
          description="Inventario jerárquico del equipo, salud por componente e historial de reemplazos. Disponible en Pro."
        >
          <ComponentesEquipo idEquipo={equipo.id} />
        </ProLockOverlay>
      ),
    },
  ].filter(Boolean);

  return (
    <Drawer
      title={
        <Space align="center" size="middle" style={{ minWidth: 0 }}>
          <div
            aria-hidden
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: token.colorPrimaryBg,
              color: token.colorPrimary,
              flexShrink: 0,
            }}
          >
            <ApiOutlined style={{ fontSize: 18 }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <Title level={5} style={{ margin: 0 }}>
              {equipo.nombre || `Equipo #${equipo.id}`}
            </Title>
            <Space size={6} wrap>
              <Tag color={TIPO_EQUIPO_COLORS[equipo.tipo_equipo_categoria] ?? 'default'}>
                {TIPO_EQUIPO_LABELS[equipo.tipo_equipo_categoria] ?? 'Sin categoría'}
              </Tag>
              {equipo.num_registro_minvu ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  MINVU: {equipo.num_registro_minvu}
                </Text>
              ) : null}
            </Space>
          </div>
        </Space>
      }
      placement="right"
      width={820}
      open={open}
      onClose={onClose}
      destroyOnHidden
      extra={
        <Button icon={<QrcodeOutlined />} onClick={() => setQrOpen(true)}>
          QR
        </Button>
      }
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      <QrEquipoModal open={qrOpen} onClose={() => setQrOpen(false)} equipo={equipo} />
    </Drawer>
  );
}

export default EquipoDetailDrawer;
